import React from 'react';
import { Button } from '@/components/ui/button';
import { ArrowRight, ExternalLink } from 'lucide-react';
import { useSiteContent } from '@/hooks/useSiteContent';
import { Link } from 'react-router-dom';
import heroImage from '@/assets/hero-real-estate.jpg';

const Hero = () => {
  const { getContentText, getContentImage, loading } = useSiteContent();
  
  const heroImageData = getContentImage('hero-background');
  const backgroundImage = heroImageData.url || heroImage;

  if (loading) {
    return (
      <section id="home" className="relative min-h-[85vh] flex items-center overflow-hidden">
        <div className="absolute inset-0 bg-primary/20"></div>
        <div className="container relative z-10 mx-auto px-4">
          <div className="max-w-4xl">
            <div className="animate-pulse">
              <div className="h-20 bg-white/20 rounded mb-6"></div>
              <div className="h-16 bg-white/10 rounded mb-8"></div>
            </div>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section id="home" className="relative min-h-[85vh] flex items-center overflow-hidden">
      {/* Background Image - using img tag for better LCP */}
      <img 
        src={backgroundImage}
        alt=""
        fetchPriority="high"
        width="1920"
        height="1080"
        className="absolute inset-0 w-full h-full object-cover scale-105"
      />
      
      {/* Subtle gray gradient overlay */}
      <div className="absolute inset-0 bg-gradient-to-r from-gray-900/60 via-gray-900/40 to-gray-900/20"></div>
      
      {/* Subtle pattern overlay */}
      <div className="absolute inset-0 pattern-subtle opacity-30"></div>
      
      {/* Content */}
      <div className="container relative z-10 mx-auto px-4 sm:px-6 lg:px-8 py-16 lg:py-20">
        <div className="max-w-4xl animate-fade-in-up">
          {/* Decorative accent line */}
          <div className="divider-elegant mb-6 lg:mb-8"></div>
          
          <h1 className="font-serif text-white mb-6 lg:mb-8 leading-[1.1] tracking-tight">
            {getContentText('hero-title', 'Een vertrouwd adres voor onze huurders')}
          </h1>
          
          <p className="font-sans text-white/90 mb-8 lg:mb-10 leading-relaxed max-w-2xl font-light text-lg md:text-xl lg:text-2xl">
            {getContentText('hero-subtitle', 'Met persoonlijke aandacht en heldere processen houden we onze panden in goede staat en blijven we goed bereikbaar.')}
          </p>
          
          <div className="flex flex-col sm:flex-row gap-4">
            <Button 
              size="lg" 
              className="btn-luxury group px-8 py-6 text-base font-semibold rounded-lg"
              asChild
            >
              <Link to="/verhuur">
                Bekijk Aanbod
                <ArrowRight className="ml-2 h-5 w-5 transition-transform duration-300 group-hover:translate-x-1" />
              </Link>
            </Button>
            <Button 
              size="lg" 
              variant="outline" 
              className="border-2 border-white/40 text-white bg-white/5 hover:bg-white hover:text-primary font-medium backdrop-blur-sm px-8 py-6 text-base rounded-lg transition-elegant hover:border-white"
              asChild
            >
              <a href="https://stichting-croostenburght.vastgoed.app" target="_blank" rel="noopener noreferrer">
                <ExternalLink className="mr-2 h-5 w-5" />
                Huurders Portal
              </a>
            </Button>
          </div>
        </div>
      </div>

      {/* Smooth bottom transition */}
      <div className="absolute bottom-0 left-0 w-full h-32 bg-gradient-to-t from-background via-background/70 to-transparent"></div>
    </section>
  );
};

export default Hero;